import { useCallback, PropsWithChildren, useEffect, useState } from 'react';
import { Map } from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Box, debounce } from '@mui/material';
import { getMapOptions } from './mapOptions';
import {
    AzureMap,
    MapTheme,
} from '@chr/online-shared-ui-maps';
import { TrackingMapProvider } from './TrackingMapProvider';
import { MapTypes } from './MapThemeProvider';

export type AzureMapOptions = ConstructorParameters<typeof Map>[1];

type AzureTrackingMapProps = PropsWithChildren<{
    onMapReady: (map: Map) => void | Promise<void>;
    map: Map;
}>;

export const AzureTrackingMap = ({ children, onMapReady, map }: AzureTrackingMapProps) => {
    const [options] = useState(getMapOptions());

    const [mapHeight, setMapHeight] = useState(600);

    const [isTrafficEnabled, setIsTrafficEnabled] = useState(false);
    const [isWeatherEnabled, setIsWeatherEnabled] = useState(false);
    const [isDisruptionsEnabled, setIsDisruptionsEnabled] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [mapType, setMapType] = useState(MapTypes.road);
    const [navbarHeight, setNavbarHeight] = useState(0);

    const resizeMap = useCallback(() => {
        const navbarHeight = document.getElementsByTagName('nav')[0]?.clientHeight ?? 0;
        const fullscreenHeight = window.innerHeight - navbarHeight;

        setNavbarHeight(navbarHeight);
        setMapHeight(fullscreenHeight);
    }, [setNavbarHeight, setMapHeight]);

    useEffect(() => {
        if (isFullScreen) {
            resizeMap();
        }
    }, [isFullScreen, resizeMap]);

    useEffect(() => {
        const handleResize = debounce(resizeMap, 250);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [resizeMap]);

    if (options === null) {
        return null;
    }

    return (
        <Box
            sx={
                isFullScreen
                    ? {
                        position: 'fixed',
                        top: navbarHeight,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                    }
                    : {}
            }
        >
            <TrackingMapProvider
                value={{
                    isFullScreen,
                    setIsFullScreen,
                    mapType,
                    setMapType,
                    isTrafficEnabled,
                    isDisruptionsEnabled,
                    isWeatherEnabled,
                    setIsDisruptionsEnabled,
                    setIsWeatherEnabled,
                    setIsTrafficEnabled,
                    map
                }}
            >
                <AzureMap
                    options={options}
                    height={isFullScreen ? mapHeight : 600}
                    theme={mapType === MapTypes.road ? MapTheme.light : MapTheme.dark}
                    onReady={onMapReady}
                >
                    {children}
                </AzureMap>
            </TrackingMapProvider>
        </Box>
    );
};
